import { useCallback, useEffect, useState } from 'react';
import { useIonViewWillEnter } from "@ionic/react";
import { AnnotationModel, AnnotationPosition, AnnotationType } from "../models/annotation.model";
import {
  AnnotationStatus,
  AnnotationUpdatedFields,
  CreationAnnotationViewWithoutLocation,
  UpdateAnnotationViewWithoutLocation,
} from '../types/annotation.types';
import { useAnnotationFormContext } from '../contexts/annotation-form.context';
import { useAnnotationLibrary } from '../../annotation/contexts/annotation-library.context';
import { useTranslation } from 'react-i18next';

export const useAnnotations = (
  planId: string,
  getAnnotations: () => Promise<AnnotationModel[]>,
  createAnnotation: (annotation: CreationAnnotationViewWithoutLocation) => Promise<Response>,
  updateAnnotation: (annotationId: string, annotation: UpdateAnnotationViewWithoutLocation) => Promise<Response>,
  deleteAnnotation: (annotationId: string, annotationFileName: string | null) => Promise<Response>,
) => {
  const { t } = useTranslation();
  const getLabelFromRoomId = useCallback(
    (roomId: string) =>
      roomId === planId
        ? t('plan.annotations.name.outside')
        : GetLabel('nc_' + roomId) || t('plan.annotations.name.facade'),
    [planId, t]
  );
  const [annotations, setAnnotations] = useState<AnnotationModel[]>([]);
  const [currentFilterStatusValue, setCurrentFilterStatusValue] = useState<AnnotationStatus[]>(['NEW', 'PENDING', 'TODO', 'IN_PROGRESS']);
  const {isAnnotationFormVisible, setAnnotationFormVisibility} = useAnnotationFormContext();
  const { addAnnotations } = useAnnotationLibrary();

  // Fonction pour alimenter la bibliothèque
  const feedLibrary = useCallback((annotations: AnnotationModel[]) => {
    const libraryItems = annotations.map(annotation => ({
      id: annotation.id,
      title: annotation.title,
      content: annotation.content,
    }));
    addAnnotations(libraryItems);
  }, [addAnnotations]);

  useIonViewWillEnter(() => {
    getAnnotations().then((response) => {
      setAnnotations(response);
      feedLibrary(response);
    })
  });

  const filteredAnnotations = annotations.filter(annotation => 
    currentFilterStatusValue.includes(annotation.status as AnnotationStatus)
  );

  const createAnnotationThenGetAnnotationId = useCallback(
    async (
      annotationPosition: AnnotationPosition,
      type: AnnotationType,
      title: string,
      content?: string,
      pictureBinary?: string,
      status?: string,
      equipmentCode?: string
    ) => {
      const annotationCreationPayload: CreationAnnotationViewWithoutLocation = {
        roomId: annotationPosition.roomId,
        type,
        title,
        content: content ?? "",
        x: annotationPosition.x,
        y: annotationPosition.y,
        pictureBinary,
        status,
        equipmentCode
      };
      await createAnnotation(annotationCreationPayload);
      await getAnnotations().then((response) => {
        setAnnotations(response);
        feedLibrary(response);
      });
    },
    [setAnnotations, createAnnotation, getAnnotations, feedLibrary]
  );

  const updateAnnotationThenOptimisticUpdate = useCallback(
    async (annotationId: string, updatedAnnotationFields: Partial<AnnotationUpdatedFields>) => {
      await updateAnnotation(annotationId, updatedAnnotationFields);
      setAnnotations((annotations) =>
        annotations.map((existingAnnotation) =>
          existingAnnotation.id !== annotationId
            ? existingAnnotation
            : new AnnotationModel(
                annotationId,
                existingAnnotation.roomId,
                updatedAnnotationFields.type ?? existingAnnotation.type,
                updatedAnnotationFields.title ?? existingAnnotation.title,
                updatedAnnotationFields.content ?? existingAnnotation.content,
                existingAnnotation.x,
                existingAnnotation.y,
                updatedAnnotationFields.pictureBinary === undefined
                  ? existingAnnotation.pictureBinary
                  : updatedAnnotationFields.pictureBinary === ""
                  ? null
                  : updatedAnnotationFields.pictureBinary,
              updatedAnnotationFields.status ? updatedAnnotationFields.status : existingAnnotation.status,
              existingAnnotation.fileName ? existingAnnotation.fileName : null,
              existingAnnotation.location ? existingAnnotation.location : null,
              existingAnnotation.date ? existingAnnotation.date : null,
              existingAnnotation.siteId ? existingAnnotation.siteId : null,
              existingAnnotation.siteCode ? existingAnnotation.siteCode : null,
              existingAnnotation.addedBy ? existingAnnotation.addedBy : null,
              existingAnnotation.equipmentCode ? existingAnnotation.equipmentCode : null,
              existingAnnotation.portfolio ? existingAnnotation.portfolio : null
              )
        )
      );
    },
    [setAnnotations, updateAnnotation]
  );
  const deleteAnnotationThenGetAnnotations = useCallback(
    async (id: string, fileName: string | null) => {
      await deleteAnnotation(id,fileName);
      await getAnnotations().then((response) => {
        setAnnotations(response);
        feedLibrary(response);
      });
    },
    [setAnnotations, getAnnotations, deleteAnnotation, feedLibrary]
  );

  const [annotationToVisualize, setAnnotationToVisualize] = useState<AnnotationModel | null>(null);
  const [annotationPositionToCreate, setAnnotationPositionToCreate] = useState<AnnotationPosition | null>(null);
  const [annotationPositionName, setAnnotationPositionName] = useState("");
  const saveClickedPosition = useCallback(
    (annotationPosition: AnnotationPosition) => {
      setAnnotationPositionToCreate(annotationPosition);
      setAnnotationPositionName(GetLabel("nc_" + annotationPosition.roomId));
    },
    [setAnnotationPositionToCreate]
  );
  const createAnnotationOnPosition = useCallback(
    async (annotation: { type: AnnotationType; title: string; content?: string; pictureBinary?: string, status?: string, equipmentCode?: string }) => {
      await createAnnotationThenGetAnnotationId(
        annotationPositionToCreate!,
        annotation.type,
        annotation.title,
        annotation.content,
        annotation.pictureBinary,
        annotation.status,
        annotation.equipmentCode
      );
      setAnnotationFormVisibility(false);
    },
    [createAnnotationThenGetAnnotationId, setAnnotationFormVisibility, annotationPositionToCreate]
  );
  const updateAnnotationThenCloseForm = useCallback(
    async (id: string, updatedAnnotationFields: Partial<AnnotationUpdatedFields>) => {
      if (Object.keys(updatedAnnotationFields).length > 0) {
        await updateAnnotationThenOptimisticUpdate(id, updatedAnnotationFields);
      }
      setAnnotationFormVisibility(false);
      setAnnotationToVisualize(null);
    },
    [updateAnnotationThenOptimisticUpdate, setAnnotationFormVisibility, setAnnotationToVisualize]
  );
  const deleteAnnotationThenCloseAndEmptyForm = useCallback(
    async (id: string, fileName: string | null) => {
      await deleteAnnotationThenGetAnnotations(id,fileName);
      setAnnotationFormVisibility(false);
      setAnnotationToVisualize(null);
    },
    [deleteAnnotationThenGetAnnotations, setAnnotationFormVisibility, setAnnotationToVisualize]
  );
  const openFormForAnnotationCreation = useCallback(
    (annotationPosition: AnnotationPosition) => {
      setAnnotationPositionToCreate(annotationPosition);
      setAnnotationPositionName(getLabelFromRoomId(annotationPosition.roomId));
      setAnnotationFormVisibility(true);
    },
    [setAnnotationFormVisibility, setAnnotationPositionToCreate, getLabelFromRoomId]
  );
  const openFormForAnnotationUpdate = useCallback(
    (annotation: AnnotationModel) => {
      setAnnotationToVisualize(annotation);
      setAnnotationPositionName(getLabelFromRoomId(annotation.roomId));
      setAnnotationFormVisibility(true);
    },
    [setAnnotationToVisualize, setAnnotationFormVisibility, getLabelFromRoomId]
  );
  const closeAndEmptyAnnotationForm = useCallback(() => {
    setAnnotationFormVisibility(false);
    setAnnotationToVisualize(null);
  }, [setAnnotationFormVisibility, setAnnotationToVisualize]);

  useEffect(() => {
    setAnnotations(annotations)
  },[annotations,currentFilterStatusValue]);


  return {
    annotations: filteredAnnotations,
    openFormForAnnotationCreation,
    createAnnotationOnPosition,
    openFormForAnnotationUpdate,
    updateAnnotationThenCloseForm,
    deleteAnnotationThenCloseAndEmptyForm,
    closeAndEmptyAnnotationForm,
    saveClickedPosition,
    annotationToVisualize,
    isAnnotationFormVisible,
    setAnnotationFormVisibility,
    annotationPositionName,
    setAnnotationPositionName,
    currentFilterStatusValue,
    setCurrentFilterStatusValue,
  };
};
